import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Avatar,
    Grid,
    Divider,
    Chip,
    IconButton,
    CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../../Api/ApiUrls";

const StyledDialogTitle = styled(DialogTitle)({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#1976d2",
    color: "white",
});

const PropertyDetailItem = ({ label, value }) => (
    <Grid item xs={12} sm={6} md={4}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {label}:
        </Typography>
        <Typography variant="body1" gutterBottom color={value ? 'text.primary' : 'text.secondary'}>
            {value || "N/A"}
        </Typography>
    </Grid>
);

const ViewModal = ({ open, onClose, propertyId }) => {
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && propertyId) {
            fetchPropertyDetails(propertyId);
        }
        // Reset property when modal closes
        if (!open) {
            setProperty(null);
        }
    }, [open, propertyId]);

    const fetchPropertyDetails = async (id) => {
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/property/${id}/`);
            setProperty(response.data);
        } catch (error) {
            console.error("Error fetching property details:", error);
            toast.error("Failed to fetch property details");
            onClose();
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogContent>
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (!property) return null;

    const {
        property_images = [],
        power_backup,
        gated_security,
        borewell,
        parking,
        lift,
        _1bhk_count,
        _2bhk_count,
        _3bhk_count,
        _4bhk_count,
        bedrooms_count,
        bathrooms_count,
        user_first_name,
        user_last_name,
        user_mobile_no,
        user_email,
        category_name,
        type,
        min_budget,
        max_budget,
        min_acres,
        max_acres,
        ratio,
        floor,
        comment,
        no_of_flores,
        rooms_count,
        duplex_bedrooms,
        shop_count,
        house_count,
        balcony,
        advance_payment,
        boost_date,
        description,
        status,
        created_at,
        updated_at,
        username,
        user_mobile
    } = property;

    // Prepare amenities array
    const amenities = [
        { label: "Power Backup", value: power_backup },
        { label: "Gated Security", value: gated_security },
        { label: "Borewell", value: borewell },
        { label: "Parking", value: parking },
        { label: "Lift", value: lift },
    ].filter(item => item.value && item.value !== "");

    // Prepare room configuration array
    const roomConfigs = [
        { label: "1 BHK", value: _1bhk_count },
        { label: "2 BHK", value: _2bhk_count },
        { label: "3 BHK", value: _3bhk_count },
        { label: "4 BHK", value: _4bhk_count },
        { label: "Bedrooms", value: bedrooms_count },
        { label: "Bathrooms", value: bathrooms_count },
    ].filter(item => item.value && item.value !== "");

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <StyledDialogTitle>
                <Typography variant="h6">{property.property_name || "Property Details"}</Typography>
                <IconButton onClick={onClose} sx={{ color: "white" }}>
                    <CloseIcon />
                </IconButton>
            </StyledDialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                        Property Details
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={3}>
                    {/* User Information */}
                    <PropertyDetailItem 
                        label="User Name" 
                        value={user_first_name && user_last_name ? `${user_first_name} ${user_last_name}` : (username || "N/A")} 
                    />
                    <PropertyDetailItem label="User Mobile" value={user_mobile_no || user_mobile || "N/A"} />
                    <PropertyDetailItem label="User Email" value={user_email || "N/A"} />
                    
                    {/* Property Basic Details */}
                    <PropertyDetailItem label="Property Name" value={property.property_name || "N/A"} />
                    <PropertyDetailItem label="Property Type" value={property.property_type || category_name || "N/A"} />
                    <PropertyDetailItem label="Category" value={category_name || "N/A"} />
                    <PropertyDetailItem label="Type" value={type || "N/A"} />
                    <PropertyDetailItem label="Price" value={property.price ? `₹${property.price.toLocaleString()}` : "N/A"} />
                    
                    {/* Budget Information */}
                    {min_budget && (
                        <PropertyDetailItem label="Min Budget" value={`₹${min_budget.toLocaleString()}`} />
                    )}
                    {max_budget && (
                        <PropertyDetailItem label="Max Budget" value={`₹${max_budget.toLocaleString()}`} />
                    )}
                    
                    {/* Land Details */}
                    {min_acres && (
                        <PropertyDetailItem label="Min Acres" value={min_acres} />
                    )}
                    {max_acres && (
                        <PropertyDetailItem label="Max Acres" value={max_acres} />
                    )}
                    
                    <PropertyDetailItem 
                        label="Land Area" 
                        value={max_acres ? `${max_acres} ${property.units || "sq.ft"}` : "N/A"} 
                    />
                    <PropertyDetailItem 
                        label="Site Area" 
                        value={property.site_area ? `${property.site_area} ${property.units || "sq.ft"}` : "N/A"} 
                    />
                    
                    {/* Location Details */}
                    <PropertyDetailItem label="Location" value={property.location || "N/A"} />
                    <PropertyDetailItem label="Nearby" value={property.nearby || "N/A"} />
                    <PropertyDetailItem label="Facing" value={property.facing || "N/A"} />
                    
                    {/* Measurements */}
                    <PropertyDetailItem 
                        label="Road Width" 
                        value={property.roadwidth ? `${property.roadwidth} ${property.units || "ft"}` : "N/A"} 
                    />
                    <PropertyDetailItem 
                        label="Dimensions" 
                        value={property.length && property.width ? `${property.length} x ${property.width} ${property.units || "ft"}` : "N/A"} 
                    />
                    <PropertyDetailItem 
                        label="Built-up Area" 
                        value={property.buildup_area ? `${property.buildup_area} ${property.units || "sq.ft"}` : "N/A"} 
                    />
                    <PropertyDetailItem label="Ratio" value={ratio || "N/A"} />
                    
                    {/* Property Features */}
                    <PropertyDetailItem label="Floor" value={floor || "N/A"} />
                    <PropertyDetailItem label="No of Floors" value={no_of_flores || "N/A"} />
                    <PropertyDetailItem label="Rooms Count" value={rooms_count || "N/A"} />
                    <PropertyDetailItem label="Duplex Bedrooms" value={duplex_bedrooms || "N/A"} />
                    <PropertyDetailItem label="Shop Count" value={shop_count || "N/A"} />
                    <PropertyDetailItem label="House Count" value={house_count || "N/A"} />
                    <PropertyDetailItem label="Balcony" value={balcony || "N/A"} />
                    
                    {/* Payment and Status */}
                    <PropertyDetailItem label="Advance Payment" value={advance_payment ? `₹${advance_payment.toLocaleString()}` : "N/A"} />
                    <PropertyDetailItem label="Comment" value={comment || "N/A"} />
                    <PropertyDetailItem label="Description" value={description || "N/A"} />
                    
                    {/* Contact Information */}
                    <PropertyDetailItem label="Mobile No" value={property.mobile_no || "N/A"} />
                    <PropertyDetailItem label="Posted By" value={property.posted_by || "N/A"} />
                    
                    {/* Status and Admin Info */}
                    <PropertyDetailItem label="Admin Status" value={property.Admin_status || "N/A"} />
                    <PropertyDetailItem label="Status" value={status ? "Active" : "Inactive"} />
                    
                    {/* Coordinates */}
                    <PropertyDetailItem 
                        label="Coordinates" 
                        value={property.lat && property.long ? `Lat: ${property.lat}, Long: ${property.long}` : "N/A"} 
                    />
                    
                    {/* Dates */}
                    <PropertyDetailItem 
                        label="Boost Date" 
                        value={boost_date ? new Date(boost_date).toLocaleDateString() : "N/A"} 
                    />
                    <PropertyDetailItem 
                        label="Created At" 
                        value={created_at ? new Date(created_at).toLocaleDateString() : "N/A"} 
                    />
                    <PropertyDetailItem 
                        label="Updated At" 
                        value={updated_at ? new Date(updated_at).toLocaleDateString() : "N/A"} 
                    />

                    {/* Amenities Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Amenities:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {amenities.length > 0 ? (
                                amenities.map((item, idx) => (
                                    <Chip key={idx} label={`${item.label}: ${item.value}`} />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">No amenities listed</Typography>
                            )}
                        </Box>
                    </Grid>

                    {/* Room Configuration Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Room Configuration:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {roomConfigs.length > 0 ? (
                                roomConfigs.map((item, idx) => (
                                    <Chip key={idx} label={`${item.label}: ${item.value}`} />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">No room configuration available</Typography>
                            )}
                        </Box>
                    </Grid>

                    {/* Images Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Property Images:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', py: 2 }}>
                            {property_images.length > 0 ? (
                                property_images.map((image) => (
                                    <Avatar
                                        key={image.id}
                                        src={`${BASE_URL}${image.image}`}
                                        alt={`Property ${image.id}`}
                                        variant="rounded"
                                        sx={{ width: 150, height: 150 }}
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">No images available</Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewModal;